import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import colors from 'theme/colors';
import menuApi from 'api/requests/menu';

const TYPE_CONFIG = {
    percentage: { suffix: '%',  label: 'Percentual de desconto', placeholder: 'Ex: 20' },
    fixed:      { prefix: 'R$', label: 'Valor do desconto',      placeholder: 'Ex: 5.00' },
    fixed_price:{ prefix: 'R$', label: 'Preço promocional',      placeholder: 'Ex: 10.00' },
};

export default function StepDiscount({ data, onChange, slug }) {
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const isBundle = data.type === 'bundle';

    function set(key, value) {
        onChange({ ...data, [key]: value });
    }

    useEffect(() => {
        if (!isBundle || !slug) return;
        setLoadingProducts(true);
        menuApi.getProducts(slug)
            .then(res => setProducts(res.data.data || []))
            .catch(() => {})
            .finally(() => setLoadingProducts(false));
    }, [isBundle, slug]);

    if (!isBundle) {
        const cfg = TYPE_CONFIG[data.type] || TYPE_CONFIG.percentage;
        return (
            <Container>
                <Field>
                    <Label>{cfg.label} *</Label>
                    <InputWrap>
                        {cfg.prefix && <Affix>{cfg.prefix}</Affix>}
                        <ValueInput
                            type='number'
                            step='0.01'
                            min='0'
                            value={data.discount_value}
                            onChange={e => set('discount_value', e.target.value)}
                            placeholder={cfg.placeholder}
                            autoFocus
                            $hasPrefix={!!cfg.prefix}
                            $hasSuffix={!!cfg.suffix}
                        />
                        {cfg.suffix && <Affix>{cfg.suffix}</Affix>}
                    </InputWrap>
                    {data.type === 'percentage' && (
                        <Hint>Valor entre 1 e 100</Hint>
                    )}
                    {data.type === 'fixed_price' && (
                        <Hint>Preço final cobrado ao cliente</Hint>
                    )}
                </Field>
            </Container>
        );
    }

    return (
        <Container>
            <BundleHint>
                Configure quais produtos participam do combo e as quantidades.
            </BundleHint>

            <TwoCol>
                <Field>
                    <Label>Produto comprado *</Label>
                    <Select
                        value={data.buy_menu_product_id ?? ''}
                        onChange={e => set('buy_menu_product_id', e.target.value ? Number(e.target.value) : null)}
                        disabled={loadingProducts}
                    >
                        <option value=''>
                            {loadingProducts ? 'Carregando...' : 'Selecionar'}
                        </option>
                        {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Select>
                </Field>

                <Field>
                    <Label>Quantidade mínima *</Label>
                    <Input
                        type='number'
                        min='1'
                        value={data.buy_quantity}
                        onChange={e => set('buy_quantity', e.target.value)}
                        placeholder='Ex: 2'
                    />
                </Field>
            </TwoCol>

            <Divider />

            <TwoCol>
                <Field>
                    <Label>Produto grátis *</Label>
                    <Select
                        value={data.get_menu_product_id ?? ''}
                        onChange={e => set('get_menu_product_id', e.target.value ? Number(e.target.value) : null)}
                        disabled={loadingProducts}
                    >
                        <option value=''>
                            {loadingProducts ? 'Carregando...' : 'Selecionar'}
                        </option>
                        {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Select>
                    <Hint>Pode ser o mesmo produto</Hint>
                </Field>

                <Field>
                    <Label>Quantidade grátis *</Label>
                    <Input
                        type='number'
                        min='1'
                        value={data.get_quantity}
                        onChange={e => set('get_quantity', e.target.value)}
                        placeholder='Ex: 1'
                    />
                </Field>
            </TwoCol>
        </Container>
    );
}

const Container = styled.div`display: flex; flex-direction: column; gap: 18px;`;

const Field = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const Label = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Hint = styled.span`font-size: 11px; color: ${colors.nightLight};`;

const BundleHint = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0;
    padding: 12px 14px;
    background: ${colors.occasionalOrange}0D;
    border-radius: 10px;
    border-left: 3px solid ${colors.occasionalOrange};
`;

const InputWrap = styled.div`
    display: flex;
    align-items: center;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
    transition: border-color 0.15s;
    &:focus-within { border-color: ${colors.occasionalPurple}; }
`;

const Affix = styled.span`
    padding: 12px 12px;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightLight};
    background: ${colors.neutralDark};
    flex-shrink: 0;
`;

const ValueInput = styled.input`
    flex: 1;
    border: none;
    padding: 12px 14px;
    font-size: 18px;
    font-weight: 700;
    outline: none;
    color: ${colors.nightDark};
    background: transparent;
    font-variant-numeric: tabular-nums;
    &::placeholder { color: #bbb; font-weight: 400; font-size: 15px; }
`;

const Input = styled.input`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 15px;
    outline: none;
    color: ${colors.nightDark};
    background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: #999; }
`;

const Select = styled.select`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 15px;
    background: #fff;
    outline: none;
    color: ${colors.nightDark};
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    align-items: start;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.neutralDark};
    margin: 0 -4px;
`;
